import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calculator,
  IndianRupee,
  MessageCircle,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Info,
  ArrowRight,
} from "lucide-react";
import { CLINIC, waLink } from "../lib/clinic";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import {
  SITE,
  breadcrumb,
  graph,
  CLINIC_NODE,
  AUTHOR_REF,
  PUBLISHER_REF,
} from "../lib/schema";
import SectionHeader from "../components/site/SectionHeader";
import AuthorCard from "../components/site/AuthorCard";
import MedicalDisclaimer from "../components/site/MedicalDisclaimer";

// ---------------------------------------------------------------------------
// Interactive Cataract Surgery Cost Calculator (Varanasi).
// Money-intent link-magnet designed to rank for "cataract surgery calculator",
// "cataract cost calculator Varanasi", "मोतियाबिंद खर्च कैलकुलेटर".
// Emits SoftwareApplication + FAQPage + Service/Offer schema.
// ---------------------------------------------------------------------------

const CANONICAL_PATH = "/tools/cataract-cost-calculator";

// Canonical price tiers — MUST stay in sync with /cataract-surgery-cost-in-varanasi
// and /iol-selector.  All prices are per-eye INR ranges.
const IOL_TIERS = [
  {
    id: "standard-monofocal",
    name: "Standard Monofocal IOL",
    tagline: "Single distance vision; reading glasses needed",
    min: 18000,
    max: 25000,
  },
  {
    id: "aspheric-monofocal",
    name: "Aspheric Monofocal (premium optics)",
    tagline: "Sharper night driving, single-focus",
    min: 25000,
    max: 35000,
  },
  {
    id: "extended-monofocal",
    name: "Extended Monofocal (functional near-intermediate)",
    tagline: "Slightly extended range, no full multifocal",
    min: 40000,
    max: 45000,
  },
  {
    id: "toric-monofocal",
    name: "Toric Monofocal (astigmatism correction)",
    tagline: "For patients with cylindrical prescription > 1.5 D",
    min: 40000,
    max: 60000,
  },
  {
    id: "edof",
    name: "EDOF — Extended Depth-of-Focus IOL",
    tagline: "Distance + intermediate (dashboard, computer)",
    min: 50000,
    max: 100000,
  },
  {
    id: "trifocal",
    name: "Trifocal / Panoptix (multifocal)",
    tagline: "Glasses-free at all distances",
    min: 70000,
    max: 110000,
  },
];

// Insurance / payment scenarios. Self-pay = full price. Private cashless
// insurance: the policy covers a package of approx ₹25,000–30,000 depending
// on the policy and other factors, and the patient bears the package cost
// difference for premium options.
// Ayushman Bharat (PM-JAY) is deliberately NOT a calculator option.
// Per clinic policy the scheme covers only an IOL implant worth approx
// ₹3,000 and no category mapping or out-of-pocket estimate is published —
// scheme specifics are clarified at the hospital.
const PAYERS = [
  {
    id: "self-pay",
    name: "Self-pay (no insurance)",
    type: "full",
    note: "Patient pays 100% of the surgery + IOL cost.",
  },
  {
    id: "private-insurance",
    name: "Private cashless insurance (Star, HDFC Ergo, Care, etc.)",
    type: "package",
    coverMin: 25000,
    coverMax: 30000,
    note: "Most private policies cover a cataract package of approx ₹25,000–30,000. For any package above that, the difference is borne by you — the exact amount depends on your individual policy and the final authorisation given by the insurance company.",
  },
];

const CURRENCY = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

const rangeCost = (tier, eyeCount, payer) => {
  // Private insurance: policy covers a package of approx ₹25,000–30,000.
  // Patient bears the difference between the gross bill and the package
  // (best case: highest cover against lowest tier price; worst case:
  // lowest cover against highest tier price). Floored at ₹0.
  if (payer?.type === "package") {
    return {
      min: Math.max(0, (tier.min - payer.coverMax) * eyeCount),
      max: Math.max(0, (tier.max - payer.coverMin) * eyeCount),
      note: "Assumes your policy covers a ₹25,000–30,000 package per eye; the difference is borne by you. The exact amount depends on your individual policy and the final authorisation given by the insurance company.",
    };
  }
  return {
    min: tier.min * eyeCount,
    max: tier.max * eyeCount,
    note: null,
  };
};

const grossRange = (tier, eyeCount) => ({
  min: tier.min * eyeCount,
  max: tier.max * eyeCount,
});

// FAQ block — powers the FAQPage schema for rich results.
const CALC_FAQS = [
  {
    q: "How accurate is this cataract surgery cost calculator?",
    a: "Estimates are based on published Shantanu Netralaya IOL price ranges — the same tiers we quote in OPD. For private insurance, we assume your policy covers a package of approx ₹25,000–30,000 and the difference is borne by you — the exact amount depends on your individual policy and the final authorisation given by the insurance company. Final invoices also depend on the exact IOL model (brand), biometry findings, and any add-on procedures like YAG capsulotomy later. We're happy to send a written itemised estimate over WhatsApp once we know your biometry.",
  },
  {
    q: "Is bilateral (both-eye) cataract surgery cheaper than one eye at a time?",
    a: "Cost-wise, each eye is billed as a separate procedure — there's no bundled discount because each surgery uses its own IOL, OT time and consumables. Most patients choose to have one eye done at a time (2-3 weeks apart) so the operated eye can heal and give visual feedback before the second eye. Some low-risk patients opt for same-day bilateral surgery to save on travel.",
  },
  {
    q: "Does the calculator include OPD visits, biometry and medicines?",
    a: "No. The calculator shows the IOL + surgery + OT bundle only. Pre-op biometry (IOL power calculation) is a separate OPD investigation billed at ₹1,500–₹2,500. Post-op eye drops for 4-6 weeks typically cost ₹800–₹1,500. YAG capsulotomy (if needed 6-24 months later) is billed separately at ₹3,000–₹4,000.",
  },
  {
    q: "I have an Ayushman Bharat card. What does the scheme cover for cataract surgery?",
    a: "Ayushman Bharat (PM-JAY) covers only an IOL implant worth approximately ₹3,000 for eligible beneficiaries. The scheme does not permit upgrades. Which lens option applies to your case, and any out-of-pocket amount, is clarified at the hospital after your examination — message us on WhatsApp to discuss before you visit.",
  },
  {
    q: "Why do premium IOLs like Trifocal cost so much more than standard monofocals?",
    a: "Trifocal and EDOF IOLs use proprietary diffractive optics manufactured by only a handful of global companies (Alcon Panoptix, Zeiss AT LISA, Johnson & Johnson Tecnis Synergy). The lens itself costs the surgeon between ₹35,000 and ₹85,000 depending on brand and platform. Standard monofocals are commoditised and cost the surgeon ₹1,500-₹4,000. That upstream cost difference flows through to the patient invoice.",
  },
  {
    q: "Can I get a written estimate to compare with other hospitals in Varanasi?",
    a: "Yes — we send a written itemised estimate on WhatsApp before you commit to surgery. Share your biometry report (if done) or just message us with your prescription and age, and we'll return a printable quote within a couple of hours during clinic time.",
  },
];

const CataractCalculator = () => {
  const [eyeCount, setEyeCount] = useState(1);
  const [tierId, setTierId] = useState(IOL_TIERS[0].id);
  const [payerId, setPayerId] = useState(PAYERS[0].id);

  const tier = IOL_TIERS.find((t) => t.id === tierId) || IOL_TIERS[0];
  const payer = PAYERS.find((p) => p.id === payerId) || PAYERS[0];
  const gross = grossRange(tier, eyeCount);
  const outOfPocket = rangeCost(tier, eyeCount, payer);

  usePageMeta({
    title:
      "Cataract Surgery Cost Calculator (Varanasi) — Get Your IOL Estimate | Dr. Shantanu Kumar Gupta",
    path: CANONICAL_PATH,
    description:
      "Free interactive cataract surgery cost calculator for Varanasi. Get an instant per-eye estimate for standard monofocal (₹18,000+), toric, EDOF and trifocal IOLs with self-pay and private cashless insurance scenarios. Shantanu Netralaya — MICS phaco, cashless accepted.",
  });

  const jsonLd = useMemo(
    () =>
      graph([
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Tools", path: "/tools/cataract-cost-calculator" },
          { name: "Cataract Cost Calculator", path: CANONICAL_PATH },
        ]),
        CLINIC_NODE,
        {
          "@type": "SoftwareApplication",
          "@id": `${SITE}${CANONICAL_PATH}#tool`,
          name: "Cataract Surgery Cost Calculator (Varanasi)",
          applicationCategory: "MedicalApplication",
          operatingSystem: "Any (web)",
          url: `${SITE}${CANONICAL_PATH}`,
          publisher: PUBLISHER_REF,
          author: AUTHOR_REF,
          offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
          description:
            "Interactive calculator that estimates cataract surgery cost per eye at Shantanu Netralaya, Varanasi, across all IOL tiers (standard monofocal to trifocal) with self-pay and private cashless insurance scenarios.",
          featureList: [
            "6 IOL tier options (monofocal → trifocal)",
            "Bilateral vs single-eye calculation",
            "Private cashless insurance package-difference estimate",
            "WhatsApp quote request",
          ],
        },
        {
          "@type": "MedicalWebPage",
          "@id": `${SITE}${CANONICAL_PATH}#page`,
          url: `${SITE}${CANONICAL_PATH}`,
          name: "Cataract Surgery Cost Calculator — Varanasi",
          inLanguage: "en-IN",
          author: AUTHOR_REF,
          reviewedBy: AUTHOR_REF,
          publisher: PUBLISHER_REF,
          specialty: { "@type": "MedicalSpecialty", name: "Ophthalmology" },
          about: {
            "@type": "MedicalProcedure",
            name: "Phacoemulsification cataract surgery with intraocular lens (IOL) implantation",
          },
        },
        // Service + Offer catalog — pricing schema per IOL tier.
        {
          "@type": "Service",
          "@id": `${SITE}${CANONICAL_PATH}#service`,
          serviceType: "Cataract Surgery (MICS Phacoemulsification)",
          provider: PUBLISHER_REF,
          areaServed: [
            { "@type": "City", name: "Varanasi" },
            { "@type": "AdministrativeArea", name: "Purvanchal" },
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "IOL tier pricing",
            itemListElement: IOL_TIERS.map((t) => ({
              "@type": "Offer",
              name: t.name,
              description: t.tagline,
              priceCurrency: "INR",
              priceSpecification: {
                "@type": "PriceSpecification",
                minPrice: t.min,
                maxPrice: t.max,
                priceCurrency: "INR",
                unitText: "per eye",
              },
            })),
          },
        },
        {
          "@type": "FAQPage",
          "@id": `${SITE}${CANONICAL_PATH}#faq`,
          mainEntity: CALC_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ]),
    []
  );
  usePageJsonLd(jsonLd);

  const whatsAppQuote = waLink(
    `Hi, I used the Cataract Surgery Cost Calculator on drshantanugupta.com.\n\n` +
      `• Eye(s): ${eyeCount === 2 ? "Both eyes" : "One eye"}\n` +
      `• Preferred IOL: ${tier.name}\n` +
      `• Payment: ${payer.name}\n` +
      `• Estimated out-of-pocket: ${CURRENCY(outOfPocket.min)} – ${CURRENCY(outOfPocket.max)}\n\n` +
      `Please send me a written itemised quote.`
  );

  return (
    <div data-testid="cataract-calculator-page">
      {/* Hero */}
      <section className="pt-20 pb-8 container-page">
        <div className="overline mb-4 inline-flex items-center gap-2">
          <Calculator size={14} strokeWidth={1.5} /> Free interactive tool
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          Cataract Surgery Cost Calculator — Varanasi
        </h1>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
          Choose your IOL, choose your payment scenario — get a realistic
          out-of-pocket estimate in seconds. Prices reflect real IOL tiers at
          Shantanu Netralaya, honestly quoted. No email or sign-up required.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={12} /> Reviewed by Dr. Shantanu Kumar Gupta
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Sparkles size={12} /> Ayushman Bharat accepted — details at hospital
          </span>
        </div>
      </section>

      {/* Calculator */}
      <section
        className="py-6 container-page"
        data-testid="calculator-section"
      >
        <div className="rounded-3xl border border-[hsl(var(--primary))]/25 bg-gradient-to-br from-[hsl(var(--primary))]/6 via-background to-[hsl(var(--accent))]/6 p-6 md:p-10">
          <div className="grid gap-8 md:grid-cols-[1.1fr_1fr]">
            {/* Inputs */}
            <div className="space-y-6">
              {/* Eye count */}
              <fieldset data-testid="input-eye-count">
                <legend className="overline mb-3 inline-flex items-center gap-2">
                  <Info size={12} /> Which eye(s) need surgery?
                </legend>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { v: 1, label: "One eye" },
                    { v: 2, label: "Both eyes" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setEyeCount(o.v)}
                      data-testid={`eye-option-${o.v}`}
                      className={`p-4 rounded-xl border text-left transition ${
                        eyeCount === o.v
                          ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 ring-1 ring-[hsl(var(--primary))]/40"
                          : "border-border hover:border-[hsl(var(--primary))]/40"
                      }`}
                    >
                      <div className="font-serif text-lg">{o.label}</div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        {o.v === 2
                          ? "Usually staged 2-3 weeks apart"
                          : "Second eye can be quoted separately later"}
                      </div>
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* IOL tier */}
              <fieldset data-testid="input-iol-tier">
                <legend className="overline mb-3 inline-flex items-center gap-2">
                  <Sparkles size={12} /> Choose your IOL (lens) tier
                </legend>
                <div className="space-y-2">
                  {IOL_TIERS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTierId(t.id)}
                      data-testid={`tier-option-${t.id}`}
                      className={`w-full p-4 rounded-xl border text-left transition flex items-start gap-3 ${
                        tierId === t.id
                          ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 ring-1 ring-[hsl(var(--primary))]/40"
                          : "border-border hover:border-[hsl(var(--primary))]/40"
                      }`}
                    >
                      <div
                        className={`mt-1 h-3 w-3 rounded-full shrink-0 ${
                          tierId === t.id
                            ? "bg-[hsl(var(--primary))]"
                            : "border border-border"
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <div className="font-medium">{t.name}</div>
                        </div>
                        <div className="text-[11.5px] text-muted-foreground mt-0.5">
                          {t.tagline}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1.5">
                          Base range: <span className="font-medium text-foreground/80">{CURRENCY(t.min)} – {CURRENCY(t.max)}</span> / eye
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* Payer */}
              <fieldset data-testid="input-payer">
                <legend className="overline mb-3 inline-flex items-center gap-2">
                  <ShieldCheck size={12} /> Payment / insurance scenario
                </legend>
                <div className="space-y-2">
                  {PAYERS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPayerId(p.id)}
                      data-testid={`payer-option-${p.id}`}
                      className={`w-full p-3.5 rounded-xl border text-left transition ${
                        payerId === p.id
                          ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 ring-1 ring-[hsl(var(--primary))]/40"
                          : "border-border hover:border-[hsl(var(--primary))]/40"
                      }`}
                    >
                      <div className="font-medium text-sm">{p.name}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {p.note}
                      </div>
                    </button>
                  ))}
                </div>
              </fieldset>
            </div>

            {/* Live estimate */}
            <div
              className="rounded-2xl bg-background border border-border p-6 md:p-8 self-start sticky md:top-24"
              data-testid="estimate-panel"
            >
              <div className="overline mb-2 inline-flex items-center gap-2">
                <IndianRupee size={13} /> Your live estimate
              </div>
              <h2 className="font-serif text-2xl leading-tight">
                {tier.name}
              </h2>
              <div className="text-xs text-muted-foreground mt-1">
                {eyeCount === 2 ? "Both eyes" : "One eye"} · {payer.name}
              </div>

              <div className="mt-6 space-y-4">
                <div className="p-4 rounded-xl bg-[hsl(var(--secondary))]/30 border border-border">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    Gross bill (surgery + IOL)
                  </div>
                  <div
                    className="mt-1 font-serif text-2xl"
                    data-testid="estimate-gross"
                  >
                    {CURRENCY(gross.min)} – {CURRENCY(gross.max)}
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-[hsl(var(--primary))]/8 border border-[hsl(var(--primary))]/30">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                    Your out-of-pocket (estimated)
                  </div>
                  <div
                    className="mt-1 font-serif text-3xl text-[hsl(var(--primary))]"
                    data-testid="estimate-oop"
                  >
                    {outOfPocket.min === 0 && outOfPocket.max === 0
                      ? payer?.type === "package"
                        ? "₹0 (within package)"
                        : "₹0"
                      : `${CURRENCY(outOfPocket.min)} – ${CURRENCY(outOfPocket.max)}`}
                  </div>
                  {outOfPocket.note && (
                    <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                      {outOfPocket.note}
                    </div>
                  )}
                </div>

                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Estimate excludes pre-op biometry (₹1,500–₹2,500), OPD visits,
                  post-op eye drops (₹800–₹1,500) and YAG capsulotomy (₹3,000–
                  ₹4,000, if needed 6-24 months later). Final invoice depends on
                  exact IOL model and biometry findings.
                </p>
              </div>

              <a
                href={whatsAppQuote}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calc-cta-whatsapp"
                className="btn-primary mt-6 w-full justify-center inline-flex items-center gap-2"
              >
                <MessageCircle size={16} /> Get written quote on WhatsApp
              </a>
              <Link
                to="/iol-selector"
                className="mt-3 btn-outline text-xs w-full justify-center inline-flex items-center gap-2"
                data-testid="calc-cta-iol"
              >
                Not sure which IOL? Try the IOL Selector <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What's included / not */}
      <section
        className="py-14 border-t border-border container-page"
        data-testid="calc-included"
      >
        <SectionHeader
          overline="Cost breakdown"
          title="What's inside — and what's billed separately"
        />
        <div className="grid md:grid-cols-2 gap-10 mt-8">
          <div>
            <div className="overline mb-3 inline-flex items-center gap-2">
              <CheckCircle2 size={13} /> Estimate INCLUDES
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              {[
                "Micro-Incision Cataract Surgery (MICS) via 2.2 mm phacoemulsification",
                "IOL (intraocular lens) at the tier you chose",
                "Topical anaesthesia (drops only, no injection)",
                "Operating theatre + consumables + surgical team",
                "Same-day discharge for uncomplicated cases",
              ].map((i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2
                    size={15}
                    className="text-[hsl(var(--accent))] mt-0.5 shrink-0"
                  />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-3 inline-flex items-center gap-2">
              <AlertCircle size={13} /> Billed SEPARATELY
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              {[
                "Pre-op biometry (IOL power calculation) — ₹1,500 to ₹2,500",
                "OPD consultation visits (pre-op & post-op follow-ups)",
                "Post-op eye drops for 4-6 weeks — typically ₹800 to ₹1,500",
                "YAG capsulotomy laser (if needed 6-24 months post-surgery) — ₹3,000 to ₹4,000",
                "Systemic pre-anaesthesia workup (via patient's physician)",
              ].map((i) => (
                <li key={i} className="flex items-start gap-2">
                  <AlertCircle
                    size={15}
                    className="text-[hsl(var(--muted-foreground))] mt-0.5 shrink-0"
                  />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="py-14 border-t border-border container-page"
        data-testid="calc-faq"
      >
        <SectionHeader
          overline="Common questions"
          title="Frequently asked questions"
        />
        <div className="mt-6 divide-y divide-border rounded-2xl border border-border overflow-hidden">
          {CALC_FAQS.map((f) => (
            <details
              key={f.q}
              className="group open:bg-[hsl(var(--secondary))]/20"
              data-testid="calc-faq-item"
            >
              <summary className="cursor-pointer list-none p-5 flex items-start gap-4 hover:bg-[hsl(var(--secondary))]/20">
                <Info
                  size={14}
                  className="mt-1 text-[hsl(var(--accent))] shrink-0"
                />
                <div className="flex-1 font-medium text-[15px] pr-6">{f.q}</div>
                <span className="text-[hsl(var(--accent))] text-lg leading-none select-none group-open:rotate-45 transition-transform">
                  +
                </span>
              </summary>
              <div className="px-5 pb-5 pl-14 text-sm text-muted-foreground leading-relaxed">
                {f.a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* Author + secondary CTA */}
      <section className="container-page py-16 grid md:grid-cols-[1fr_320px] gap-10">
        <div>
          <h2 className="font-serif text-2xl">
            Prefer a personalised written quote?
          </h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Share your prescription or biometry report on WhatsApp and we'll
            return an itemised, insurance-adjusted quote within a couple of
            hours during clinic time. No commitment required.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={waLink(
                "Hi, I'd like a personalised cataract surgery cost estimate. My prescription / biometry is attached."
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-xs inline-flex items-center gap-2"
              data-testid="calc-bottom-whatsapp"
            >
              <MessageCircle size={14} /> WhatsApp {CLINIC.whatsapp}
            </a>
            <Link
              to="/cataract-surgery-cost-in-varanasi"
              className="btn-outline text-xs inline-flex items-center gap-2"
            >
              Full transparent pricing page
            </Link>
            <Link
              to="/appointment"
              className="btn-outline text-xs inline-flex items-center gap-2"
            >
              Book consultation
            </Link>
          </div>
          <MedicalDisclaimer />
        </div>
        <aside className="self-start">
          <AuthorCard />
        </aside>
      </section>
    </div>
  );
};

export default CataractCalculator;
